'use client';

import React, { useState } from 'react';
import clsx from 'clsx';
import { Skeleton } from '@axo/ui-core/components/Skeleton';
import styles from './skeletonImage.module.scss';

type SkeletonImageProps = {
  src: string;
  alt: string;
  className?: string;
  fallbackSrc?: string;
};

type Status = 'loading' | 'loaded' | 'error';

/**
 * SkeletonImage component
 *
 * shows skeleton till image is loaded; on load error, retries `fallbackSrc`
 * if provided, otherwise falls back to a label (instead of broken image link).
 */

export const SkeletonImage = ({
  src,
  alt,
  className,
  fallbackSrc,
}: SkeletonImageProps) => {
  const [status, setStatus] = useState<Status>('loading');
  const [useFallback, setUseFallback] = useState(false);

  const currentSrc = useFallback && fallbackSrc ? fallbackSrc : src;

  const handleError = () => {
    if (fallbackSrc && !useFallback) {
      setUseFallback(true);
      setStatus('loading');
      return;
    }
    setStatus('error');
  };

  // catches cached images where onLoad may not fire after mount
  const handleRef = (img: HTMLImageElement | null) => {
    if (img?.complete) {
      setStatus(img.naturalWidth > 0 ? 'loaded' : 'error');
    }
  };

  return (
    <div className={clsx(styles.skeletonImage, className)}>
      {status === 'loading' && <Skeleton />}
      {status === 'error' ? (
        <div className={styles.label}>{alt}</div>
      ) : (
        <img
          key={currentSrc}
          ref={handleRef}
          src={currentSrc}
          alt={alt}
          data-is-loading={status === 'loading' ? '' : undefined}
          onLoad={() => setStatus('loaded')}
          onError={handleError}
        />
      )}
    </div>
  );
};
